/**
 * **자리별 플래그 워드 — 정본 하나.** 이름의 글자 하나가 숫자 한 자리다(`jefvcahs` 의 다섯째 글자 = c 자리). 엔진은 이 워드를 정수로
 * 읽어 자리마다 나눈다(jefvcahs rsngj.f90:385-398, tlpvbfe rsngv.f90:600-635) — 선행 0 은 뜻이 없어(`10` = `00000010`) 실덱은 흔히
 * 생략한다. 자릿수·기본값(모든 자리 0)·꼴 검사·자리 읽기·자리 쓰기가 이 선언에서 나온다.
 *  - `jefvcahs` — 정션 제어 플래그(정션 카드, 파이프 정션 열, 분기·터빈·기수분리기·MTPLJUN·밸브·축압기의 정션)
 *  - `tlpvbfe` — 볼륨 제어 플래그(볼륨 카드, 파이프 볼륨 열, 교차 흐름 제어 플래그)
 * 자리마다의 값 규칙(상한을 넘는 자리 등)은 엔진이 판정한다. 폼·저장은 원문의 꼴을 바꾸지 않는다(채우지 않는다) — 채우던 동안 원문이
 * 6 자리인 덱을 열고 저장만 해도 8 자리로 바뀌어 원문이 달라졌다.
 * 전에는 자릿수·기본값을 쓰는 자리마다 글자로 적었고(`'00000000'`·7·8 자리 고정 정규식), 자리 읽기도 손으로 했다(D-32·D-42·D-78).
 * 진입 청크가 읽는 옛 저장본 이행(`savedParameters`)도 쓰므로 `domain/mars` 를 가져오지 않는 이 모듈에 둔다 — 편집기·방출 쪽은
 * `marsControlWords` 가 다시 내보내는 것을 쓴다.
 */
export type FlagWord = 'jefvcahs' | 'tlpvbfe';

/** 그 플래그 워드의 자릿수 */
export const flagWordDigits = (word: FlagWord): number => word.length;

/** 모든 자리가 0 인 꼴 — 새 컴포넌트의 기본값·화면의 자리 표시 */
export const flagWordDefault = (word: FlagWord): string => '0'.repeat(word.length);

/** 덱이 받는 꼴 — 숫자 1 ~ 자릿수 개(선행 0 생략 가능) */
export const flagWordPattern = (word: FlagWord): RegExp => new RegExp(`^[0-9]{1,${word.length}}$`);

const paddedFlags = (word: FlagWord, value: string | null | undefined): string => {
  const v = String(value ?? '').trim();
  return v === '' ? flagWordDefault(word) : v.padStart(word.length, '0');
};

const letterIndex = (word: FlagWord, letter: string): number => {
  const i = word.indexOf(letter);
  if (letter.length !== 1 || i < 0) throw new Error(`플래그 워드 ${word} 에 ${letter} 자리가 없다`);
  return i;
};

/** 그 자리의 숫자 — 값이 없으면 0(엔진은 부재를 0 으로 읽는다) */
export const flagDigit = (word: FlagWord, value: string | null | undefined, letter: string): number =>
  parseInt(paddedFlags(word, value)[letterIndex(word, letter)], 10) || 0;

/** 그 자리를 바꾼 워드 — 나머지 자리는 그대로, 결과는 자릿수를 채운 꼴 */
export const withFlagDigit = (word: FlagWord, value: string | null | undefined, letter: string, digit: number): string => {
  if (!Number.isInteger(digit) || digit < 0 || digit > 9) throw new Error(`플래그 자리 값 ${digit} 은 한 자리 숫자가 아니다`);
  const p = paddedFlags(word, value);
  const i = letterIndex(word, letter);
  return p.slice(0, i) + String(digit) + p.slice(i + 1);
};

/** 자리 글자 순서 — 화면이 자리마다 칸을 그릴 때 */
export const flagLetters = (word: FlagWord): readonly string[] => [...word];
